/* eslint-disable @typescript-eslint/naming-convention */
import axios from 'axios';
import { Base64 } from 'js-base64';

import {
    isQueryParameterPresent,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';

import {
    ApiRequestOptions,
    buildErrorResponseInfo,
    buildRequestInfo,
    buildResponseInfo,
    nullApiRequestOptions,
} from 'communication/ApiCallInfo';
import { v4 as uuid } from 'uuid';
import { formDataToJson } from 'utils/formDataUtils';
import { environment } from 'environment';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';

export interface IndigoServiceClientOptions {
    loggingService: LoggingService;
}

export interface RequestConfigHeaders {
    SessionId: string;
    MachineId: string | undefined;
    ConnectionId: number;
    CorrelationId: string;
    Application: string | undefined;
    PanelId?: string | undefined;
    ['Content-Type']?: string;
}

export interface RequestConfig {
    headers?: RequestConfigHeaders;
}

export default class IndigoServiceClient {
    public sessionId: string | undefined;
    public connectionId: number | undefined;
    public machineId: string | undefined;
    public application: string | undefined;
    public panelId: string | undefined;

    private _jSessionid = uuid();

    public get jSessionId() {
        return this._jSessionid;
    }

    private useMocks: boolean;

    private logger: Logger;

    public clearSession() {
        this.sessionId = undefined;
        this.connectionId = undefined;
        this._jSessionid = uuid();
    }

    public constructor(options: IndigoServiceClientOptions) {
        this.logger = buildLogger(
            'IndigoServiceClient',
            options.loggingService
        );

        this.useMocks =
            window.location.hostname === 'localhost' &&
            isQueryParameterPresent('usemocks', ['1', 'true']);
        if (this.useMocks) {
            this.logger.warn('Using mocks');
            getGlobalWindowVariables().buildVersion = 'mock-1.0';
        }
    }

    public async get<TResponseData>(
        url: string,
        options: ApiRequestOptions,
        queryParameters?: {
            [key: string]: string;
        }
    ): Promise<TResponseData> {
        const fullUrl = this.buildUrl(url, queryParameters);
        const config = this.requestConfig();
        const requestInfo = buildRequestInfo(
            'get',
            fullUrl,
            config.headers?.CorrelationId ?? ''
        );
        options.onPending(requestInfo);

        if (options.headers) {
            config.headers = { ...config.headers, ...options.headers };
        }

        try {
            const response = await axios.get<TResponseData>(fullUrl, {
                ...config,
            });
            options.onResponse(requestInfo.id, buildResponseInfo(response));
            return response.data;
        } catch (error) {
            options.onError(requestInfo.id, buildErrorResponseInfo(error));
            throw error;
        }
    }

    public async post<Body, ResponseData>(
        url: string,
        body?: Body,
        options: ApiRequestOptions = nullApiRequestOptions,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bodyForRequestInfoP?: any
    ): Promise<ResponseData> {
        const fullUrl = this.buildUrl(url);
        const config = this.requestConfig();
        if (options.headers) {
            config.headers = { ...config.headers, ...options.headers };
        }

        const bodyForRequestInfo = bodyForRequestInfoP ?? body;

        const requestInfo = buildRequestInfo(
            'post',
            fullUrl,
            config.headers?.CorrelationId ?? '',
            bodyForRequestInfo,
            config.headers
        );
        options.onPending(requestInfo);

        try {
            const response = await axios.post<ResponseData>(
                fullUrl,
                body,
                config
            );
            options.onResponse(requestInfo.id, buildResponseInfo(response));
            return response.data;
        } catch (error) {
            options.onError(requestInfo.id, buildErrorResponseInfo(error));
            throw error;
        }
    }

    public async postFormData<ResponseData>(
        url: string,
        formData: FormData,
        options: ApiRequestOptions = nullApiRequestOptions
    ): Promise<ResponseData> {
        options.headers = options.headers ?? {};
        options.headers['Content-Type'] = 'multipart/form-data';
        const bodyInfo = formDataToJson(formData);
        return await this.post<FormData, ResponseData>(
            url,
            formData,
            options,
            bodyInfo
        );
    }

    public async postRawText<ResponseData>(
        url: string,
        body?: string,
        options: ApiRequestOptions = nullApiRequestOptions
    ): Promise<ResponseData> {
        options.headers = {
            ...options.headers,
            'Content-Type': 'text/plain',
            'Access-Url': window.location.href,
            MachineId: this.machineId,
            Application: this.application,
            CorrelationId: uuid(),
            ...(this.panelId !== undefined ? { PanelId: this.panelId } : {}),
        };

        return this.post(url, body, options);
    }

    public async delete(
        url: string,
        options: ApiRequestOptions
    ): Promise<void> {
        const fullUrl = this.buildUrl(url);
        const config = this.requestConfig();
        const requestInfo = buildRequestInfo(
            'delete',
            fullUrl,
            config.headers?.CorrelationId ?? ''
        );
        options.onPending(requestInfo);

        try {
            const response = await axios.delete<void>(fullUrl, config);
            options.onResponse(requestInfo.id, buildResponseInfo(response));
            return response.data;
        } catch (error) {
            options.onError(requestInfo.id, buildErrorResponseInfo(error));
            throw error;
        }
    }

    public async postCredentials<ResponseData>(
        url: string,
        username: string,
        password: string,
        options: ApiRequestOptions
    ): Promise<ResponseData> {
        options.headers = {
            ...options.headers,
            Authorization: `Basic ${Base64.encode(
                [username, password].join(':')
            )}`,
            MachineId: this.machineId,
            Application: this.application,
            'Access-Url': window.location.href,
            CorrelationId: uuid(),
            ...(this.panelId !== undefined ? { PanelId: this.panelId } : {}),
        };

        return this.post(url, undefined, options);
    }

    public requestConfig(): RequestConfig {
        return this.sessionId
            ? {
                  headers: {
                      SessionId: this.sessionId,
                      MachineId: this.machineId,
                      Application: this.application,
                      ConnectionId: this.connectionId!,
                      CorrelationId: uuid(),
                      ...(this.panelId !== undefined
                          ? { PanelId: this.panelId }
                          : {}),
                  },
              }
            : {};
    }

    public buildUrl(
        url: string,
        queryParameters?: {
            [key: string]: string;
        }
    ): string {
        let fullUrl = this.appendJSessionIdToUrl(
            this.useMocks
                ? `http://localhost:3001/${url}`
                : environment.baseUrl
                ? `${environment.baseUrl}${url}`
                : url
        );
        if (queryParameters) {
            fullUrl += buildQueryString(queryParameters);
        }
        return fullUrl;
    }

    public getStreamingUrl(): string {
        return this.appendJSessionIdAndMachineIdToUrl(
            `${
                this.useMocks
                    ? `http://localhost:3001/`
                    : environment.baseUrl
                    ? `${environment.baseUrl}`
                    : environment.isDevMode
                    ? 'http://localhost:8080/'
                    : ''
            }streaming`
        );
    }

    private appendJSessionIdToUrl(fullUrl: string): string {
        if (!this.jSessionId) {
            throw new Error('jSessionId has not been set');
        }
        return `${fullUrl};jsessionid=${this.jSessionId};`;
    }

    private appendJSessionIdAndMachineIdToUrl(fullUrl: string): string {
        if (!this.jSessionId) {
            throw new Error('jSessionId has not been set');
        }
        if (!this.machineId) {
            return `${fullUrl};jsessionid=${this.jSessionId};`;
        }
        return `${fullUrl};jsessionid=${this.jSessionId};?machineId=${this.machineId}`;
    }
}

export function encodeUrl(
    url: TemplateStringsArray,
    ...expr: string[]
): string {
    let result = '';
    for (let i = 0; i < url.length - 1; i++) {
        result += url[i];
        result += encodeURIComponent(expr[i]);
    }
    result += url[url.length - 1];

    return result;
}

function buildQueryString(queryParameters: { [key: string]: string }): string {
    let ret = '';

    for (const key of Object.keys(queryParameters)) {
        if (ret === '') {
            ret = ret + '?';
        } else {
            ret = ret + '&';
        }
        ret = ret + `${key}=${encodeURIComponent(queryParameters[key])}`;
    }

    return ret;
}
